<template>
  <div>
    <template v-if="loaded">
      <div class="mb-2 mt-4">
        <h4 class="is-pulled-left">
          {{ $t("bnpl:application.signature.list.title") }}
        </h4>
      </div>

      <FieldListing
        :data="signatures"
        :columns="tableColumns"
        :actions="actions"
        :searchable="false"
        :paginated="false"
        :backend-sorting="false"
      />
    </template>

    <div v-else class="column">
      <b-skeleton width="30%" :animated="true"></b-skeleton>
      <b-skeleton width="70%" :animated="true"></b-skeleton>
      <b-skeleton width="20%" :animated="true"></b-skeleton>
      <b-skeleton width="60%" :animated="true"></b-skeleton>
      <b-skeleton width="40%" :animated="true"></b-skeleton>
      <b-skeleton width="50%" :animated="true"></b-skeleton>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import i18n from "@/plugins/i18n";
import {
  BNPL_APPLICATION_STATUS,
  SIGNATURE_CLOSE_REASON,
  SIGNATURE_STATUS,
} from "@/utils/const";
import { formatBooleanRow, localizedFormatter } from "@/utils/util";
import BnplApplicationSignatureModal from "./BnplApplicationSignatureModal";

export default {
  name: "BnplRequiredDocumentsTile",
  components: {
    FieldListing: () => import("@/components/FieldListing.vue"),
  },
  props: {
    bnplApplication: {
      type: Object,
      required: true,
    },
    offerId: {
      type: Number,
      required: true,
    },
    documents: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      fixAndGeneratedDocumentsLength: 0,
      loaded: false,
      historyModalId: null,
      tableColumns: [
        {
          field: "id",
          label: "bnpl:application.signature.list_header.id",
          format: (row) => {
            return row.id;
          },
        },
        {
          field: "status",
          label: "bnpl:application.signature.list_header.status",
          format: (row) => {
            return i18n.t(SIGNATURE_STATUS.LABEL[row.status]);
          },
        },
        {
          field: "signed",
          label: "bnpl:application.signature.list_header.signed",
          format: (row) => {
            return row.signed
              ? localizedFormatter(this.country.languageIso).dateTime(
                  row.signed
                )
              : "-";
          },
        },
        {
          field: "closeReason",
          label: "bnpl:application.signature.list_header.close_reason",
          format: (row) => {
            return row.closeReason
              ? i18n.t(SIGNATURE_CLOSE_REASON.LABEL[row.closeReason])
              : "-";
          },
        },
        {
          field: "created",
          label: "bnpl:application.signature.list_header.created",
          format: (row) => {
            return localizedFormatter(this.country.languageIso).dateTime(
              row.created
            );
          },
        },
        {
          field: "dueDate",
          label: "bnpl:application.signature.list_header.due_date",
          format: (row) => {
            return row.dueDate
              ? localizedFormatter(this.country.languageIso).dateTime(
                  row.dueDate
                )
              : "-";
          },
        },
        {
          field: "active",
          label: "bnpl:application.signature.list_header.active",
          vhtml: true,
          format: (row) => {
            return formatBooleanRow(row.active);
          },
        },
        {
          field: "createdBy",
          label: "bnpl:application.signature.list_header.created_by",
          format: (row) => {
            return row.createdBy;
          },
        },
        {
          field: "documentCount",
          label: "bnpl:application.signature.list_header.count",
          format: (row) => {
            return row.documentCount + this.fixAndGeneratedDocumentsLength;
          },
        },
      ],
      actions: [
        {
          icon: "history",
          iconPack: "fa",
          type: "is-grey-icon",
          function: (row) => this.openHistoryModal(row.id),
        },
        {
          icon: "chevron-right",
          iconPack: "fa",
          type: "is-grey-icon",
          disabled: (row) => this.canWithdraw(row),
          function: (row) => this.openModal(row.id),
        },
        {
          icon: "edit",
          iconPack: "fa",
          type: "is-grey-icon",
          disabled: (row) => !this.canWithdraw(row),
          function: (row) => this.openModal(row.id),
        },
      ],
    };
  },
  async created() {
    await this.fetch();
    let signing = this.documents.filter((d) => d.type === "SIGNING");
    let generatedDocuments = signing.filter((d) => d.origin === "GENERATED");
    let fixedDocuments = signing.filter((d) => d.origin === "FIX");

    this.fixAndGeneratedDocumentsLength += generatedDocuments.length;
    this.fixAndGeneratedDocumentsLength += fixedDocuments.length;

    this.loaded = true;
  },
  computed: {
    ...mapGetters({
      getSignatures: "bnplApplicationSignatureStore/getAll",
      country: "i18n/country",
    }),
    signatures() {
      return this.getSignatures(this.bnplApplication.id);
    },
    hasActive() {
      return this.signatures.filter((signature) => signature.active).length > 0;
    },
    hasSigned() {
      return (
        this.signatures.filter(
          (signature) => signature.status === SIGNATURE_STATUS.ENUM.SIGNED
        ).length > 0
      );
    },
    canCreateNew() {
      return (
        !this.hasActive &&
        !this.hasSigned &&
        this.bnplApplication.currentStatus !== "CLOSED" &&
        this.bnplApplication.currentStatus !== "COMPLETED"
      );
    },
  },
  methods: {
    ...mapActions({
      // download: "drawer/download",
      fetchSignatures: "bnplApplicationSignatureStore/fetchAll",
    }),
    async fetch() {
      await this.fetchSignatures(this.bnplApplication.id);
    },
    canWithdraw(row) {
      return (
        row.status === SIGNATURE_STATUS.ENUM.WAITING ||
        row.status === SIGNATURE_STATUS.ENUM.ACCEPTED ||
        row.status === SIGNATURE_STATUS.ENUM.IN_PROGRESS ||
        (row.status === SIGNATURE_STATUS.ENUM.SIGNED &&
          this.bnplApplication.currentStatus ===
            BNPL_APPLICATION_STATUS.UNDER_REVIEW)
      );
    },
    openHistoryModal(signatureId) {
      this.historyModalId = signatureId;
    },
    closeHistoryModal() {
      this.historyModalId = null;
    },
    async openModal(signatureId) {
      this.$buefy.modal.open({
        parent: this,
        component: BnplApplicationSignatureModal,
        props: {
          documents: this.documents,
          offerId: this.offerId,
          bnplApplicationId: this.bnplApplication.id,
          bnplApplicationStatus: this.bnplApplication.currentStatus,
          signatureId: signatureId,
        },
        hasModalCard: true,
        trapFocus: true,
        canCancel: false,
        events: {
          save: async () => await this.fetch(),
        },
      });
    },
  },
};
</script>

<style scoped></style>
